/**
 * PreviewSandboxBanner — one-time-per-session notice for users viewing
 * the app inside the Emergent chat-UI's embedded preview iframe.
 *
 * The Emergent preview iframe lacks the `allow-downloads` sandbox flag,
 * so every download mechanism (anchor.click, window.open of a wrapper
 * page, even native print) silently fails. There is no JS-level fix —
 * the browser blocks the file save at the sandbox layer.
 *
 * Solution: detect the preview, warn the user up front, and give them
 * a one-click escape to a real browser tab where downloads work.
 *
 * Hidden once dismissed (per-tab, via sessionStorage) so we don't nag.
 */

import { useState, useEffect } from 'react';
import { ExternalLink, X, Download } from 'lucide-react';
import { isInEmergentPreview } from '../lib/sandboxDetect';

const DISMISS_KEY = 'cb_preview_banner_dismissed_v1';

export default function PreviewSandboxBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isInEmergentPreview()) return;
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === '1') return;
    } catch { /* sessionStorage blocked — show anyway */ }
    setShow(true);
  }, []);

  const dismiss = () => {
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* */ }
    setShow(false);
  };

  const openInRealTab = () => {
    try { window.open(window.location.href, '_blank'); } catch { /* */ }
    dismiss();
  };

  if (!show) return null;

  return (
    <div
      className="bg-amber-500/10 border-b border-amber-500/30 text-amber-200 text-xs px-4 py-2 flex items-center justify-between gap-3"
      data-testid="preview-sandbox-banner"
    >
      <div className="flex items-center gap-2 min-w-0 flex-1">
        <Download className="w-3.5 h-3.5 flex-shrink-0 text-amber-400" />
        <span className="truncate">
          <strong className="text-amber-100 font-semibold">Heads up:</strong>{' '}
          file downloads & native print don't work inside this preview pane.{' '}
          <span className="hidden sm:inline">Open the app in its own tab — they work normally there.</span>
        </span>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <button
          onClick={openInRealTab}
          className="px-2.5 py-1 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 font-semibold flex items-center gap-1.5 text-[11px]"
          data-testid="preview-banner-open-tab"
        >
          <ExternalLink className="w-3 h-3" /> Open in new tab
        </button>
        <button
          onClick={dismiss}
          className="p-1 rounded hover:bg-amber-500/20 text-amber-300/80 hover:text-amber-100"
          data-testid="preview-banner-dismiss"
          aria-label="Dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
